import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Save, PlugZap, CheckCircle2, XCircle, AlertTriangle, Loader2, Copy, Power,
  HelpCircle, ExternalLink,
} from "lucide-react";
import { useAuth } from "@/context/CrmAuth";
import { useTelephony } from "@/context/Telephony";
import api, { API } from "@/lib/crmApi";
import { cn } from "@/lib/utils";
import CountrySettings from "@/crm/components/telephony/CountrySettings";
import {
  CARD, DOT, PILL, inputCls, btnPrimary, btnGhost,
} from "@/crm/components/telephony/shared";
import {
  RINGOVER_DOCS, RINGOVER_STEPS, RINGOVER_FIELD_HELP, RINGOVER_WEBHOOK_HELP, isRingover,
} from "@/crm/components/telephony/ringoverHelp";

const CHECK_LABELS = {
  api: "API", account: "Compte", phone_number: "Numéro de téléphone",
  twiml_app: "TwiML App", access_token: "Access Token", numbers: "Numéros",
  presence: "Présence", webhooks: "Webhooks", calls: "Appels",
  live_audio: "Audio live", softphone: "Softphone navigateur",
  incoming: "Appels entrants", agents: "Agents joignables",
  connectivity: "Connectivité", credentials: "Identifiants",
  public_base_url: "URL publique", twiml_app_voice_url: "Voice URL de la TwiML App",
  inbound_routing: "Routage des appels entrants", numbering: "Analyse des numéros",
  webhook_signature: "Signature des webhooks",
};

const CAP_LABELS = {
  outbound_calls: "Appels sortants", inbound_calls: "Appels entrants",
  recording: "Enregistrement", live_audio: "Audio live (IA temps réel)",
  transfer: "Transfert", hold: "Mise en attente", mute: "Muet",
  presence: "Présence", websocket_audio: "Audio WebSocket",
  browser_calls: "Softphone navigateur", dtmf: "DTMF",
};

const FIELD_LABEL = "block text-xs text-slate-500";

// Fil d'Ariane du dashboard Ringover : « Développeur → Webhooks ».
function DashboardPath({ path }) {
  return (
    <div className="flex flex-wrap items-center gap-1">
      {path.map((seg, i) => (
        <span key={seg} className="flex items-center gap-1">
          {i > 0 && <span className="text-slate-300">→</span>}
          <span className="rounded-md bg-white border border-slate-200 px-1.5 py-0.5 font-mono text-[11px] text-slate-600">{seg}</span>
        </span>
      ))}
    </div>
  );
}

function DocLink({ doc }) {
  return (
    <a href={doc.url} target="_blank" rel="noreferrer"
      className="inline-flex items-center gap-1 text-[11.5px] text-indigo-600 hover:text-indigo-700 hover:underline">
      {doc.label} <ExternalLink size={11} className="shrink-0" />
    </a>
  );
}

/** Aide dépliable sous un champ : où lire la valeur dans le dashboard Ringover. */
function FieldHelp({ fieldKey, help }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-1.5">
      <button type="button" onClick={() => setOpen((o) => !o)}
        data-testid={`provider-help-toggle-${fieldKey}`} aria-expanded={open}
        className="inline-flex items-center gap-1.5 text-[11.5px] text-indigo-600 hover:text-indigo-700">
        <HelpCircle size={13} className="shrink-0" />
        {open ? "Masquer l'aide" : "Où trouver cette valeur chez Ringover ?"}
      </button>
      {open && (
        <div data-testid={`provider-help-${fieldKey}`}
          className="mt-2 rounded-xl bg-slate-50 border border-slate-100 p-3 flex flex-col gap-2">
          {help.path && <DashboardPath path={help.path} />}
          <p className="text-xs text-slate-600 text-pretty">
            <span className="text-slate-400">Équivalence : </span>{help.equivalent}
          </p>
          {help.note && <p className="text-xs text-slate-500 text-pretty">{help.note}</p>}
          {help.doc && <DocLink doc={help.doc} />}
        </div>
      )}
    </div>
  );
}

function Field({ f, value, onChange, disabled, help }) {
  const tid = `provider-config-${f.key}`;
  const wide = f.type === "bool" || f.type === "textarea";

  let control;
  if (f.type === "bool") {
    control = (
      <label className="flex items-center gap-2 text-sm text-slate-700">
        <input type="checkbox" data-testid={tid} disabled={disabled}
          checked={value === true || value === "true"} onChange={(e) => onChange(e.target.checked)} />
        {f.label}
      </label>
    );
  } else if (f.type === "select") {
    control = (
      <label className={FIELD_LABEL}>
        {f.label}
        <select data-testid={tid} disabled={disabled} value={value || f.default || ""}
          onChange={(e) => onChange(e.target.value)} className={cn(inputCls, "mt-1")}>
          {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      </label>
    );
  } else if (f.type === "textarea") {
    control = (
      <label className={FIELD_LABEL}>
        {f.label}
        <textarea data-testid={tid} disabled={disabled} rows={2} value={value || ""}
          onChange={(e) => onChange(e.target.value)} className={cn(inputCls, "mt-1")} />
      </label>
    );
  } else {
    control = (
      <label className={FIELD_LABEL}>
        {f.label}{f.required && <span className="text-rose-500"> *</span>}
        <input data-testid={tid} disabled={disabled} type={f.secret ? "password" : "text"}
          value={value || ""} onChange={(e) => onChange(e.target.value)}
          placeholder={f.placeholder || (f.secret ? "••••" : "")}
          className={cn(inputCls, "mt-1 font-mono disabled:bg-slate-50 disabled:text-slate-400")} />
      </label>
    );
  }

  return (
    <div className={cn("min-w-0", wide && "sm:col-span-2")}>
      {control}
      {help && <FieldHelp fieldKey={f.key} help={help} />}
    </div>
  );
}

/** Ordre de mise en service Ringover, au-dessus du formulaire. */
function RingoverGuide() {
  const [open, setOpen] = useState(true);
  return (
    <div className="mb-4 rounded-xl border border-indigo-100 bg-indigo-50/50 p-3.5" data-testid="ringover-guide">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 text-[13.5px] font-medium text-slate-800">
          <HelpCircle size={15} className="shrink-0 text-indigo-600" />
          Où trouver ces paramètres chez Ringover
        </div>
        <button type="button" onClick={() => setOpen((o) => !o)} data-testid="ringover-guide-toggle"
          aria-expanded={open} className="shrink-0 text-[11.5px] text-indigo-600 hover:text-indigo-700">
          {open ? "Masquer" : "Afficher"}
        </button>
      </div>
      {open && (
        <>
          <ol className="mt-3 flex flex-col gap-2.5">
            {RINGOVER_STEPS.map((st, i) => (
              <li key={st.title} className="flex gap-2.5">
                <span className="h-5 w-5 shrink-0 rounded-full bg-indigo-600 text-white flex items-center justify-center font-mono text-[11px]">
                  {i + 1}
                </span>
                <div className="min-w-0">
                  <div className="text-[13px] font-medium text-slate-800">{st.title}</div>
                  <div className="text-xs text-slate-500 mt-0.5 text-pretty">{st.detail}</div>
                </div>
              </li>
            ))}
          </ol>
          <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5">
            <DocLink doc={{ label: "Ouvrir le dashboard Ringover", url: RINGOVER_DOCS.dashboard }} />
            <DocLink doc={{ label: "Documentation API Ringover", url: RINGOVER_DOCS.developer }} />
          </div>
        </>
      )}
    </div>
  );
}

export default function ProviderSettings() {
  const { user } = useAuth();
  // La barre d'appel suit les capacités du fournisseur actif : après un
  // enregistrement, une activation ou une auto-configuration, on lui demande
  // de se relire plutôt que d'exiger un rechargement de page.
  const telephonie = useTelephony();
  const rafraichirSoftphone = () => telephonie?.refreshProvider?.();
  const isAdmin = user?.role === "admin";
  const [list, setList] = useState(null);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [activating, setActivating] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [fixing, setFixing] = useState(false);
  const [autoconfiguring, setAutoconfiguring] = useState(false);
  const [autoconf, setAutoconf] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [selftesting, setSelftesting] = useState(false);
  const [selftestResult, setSelftestResult] = useState(null);
  const [inboundTesting, setInboundTesting] = useState(false);

  const loadList = () => api.get("/telephony/providers").then(({ data }) => {
    setList(data);
    setSelected((s) => s || data.active);
  }).catch(() => {});

  useEffect(() => { loadList(); }, []);
  useEffect(() => {
    if (!selected) return;
    setDetail(null); setTestResult(null); setAutoconf(null); setAlerts(null); setSelftestResult(null);
    api.get(`/telephony/providers/${selected}/config`).then(({ data }) => setDetail(data)).catch(() => {});
  }, [selected]);

  if (!list) return <div className="text-slate-400 text-sm">Chargement…</div>;

  const setVal = (k, v) => setDetail((d) => ({ ...d, config: { ...d.config, [k]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...detail.config };
      (detail.config_fields || []).filter((f) => f.secret).forEach((f) => {
        if (payload[f.key] === "********") delete payload[f.key];
      });
      const { data } = await api.put(`/telephony/providers/${selected}/config`, payload);
      setDetail((d) => ({ ...d, config: data.config }));
      toast.success(`Configuration ${detail.label} enregistrée`);
      // Twilio : les webhooks sont réalignés à l'enregistrement, on le montre.
      if (data.autoconfigure) {
        setAutoconf(data.autoconfigure);
        if (data.autoconfigure.changes?.length) toast.info(data.autoconfigure.detail);
      }
      loadList();
      rafraichirSoftphone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Enregistrement impossible");
    } finally { setSaving(false); }
  };

  // Aller-retour signé sur l'URL publique : c'est le seul contrôle qui prouve
  // que Twilio peut réellement joindre ce serveur (DNS, proxy, chemin,
  // signature). Le test de connexion ne parle qu'à l'API Twilio.
  const selftest = async () => {
    setSelftesting(true); setSelftestResult(null);
    try {
      const { data } = await api.post("/twilio/selftest");
      setSelftestResult(data);
      data.ok ? toast.success(data.detail) : toast.error(data.detail);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Auto-test impossible");
    } finally { setSelftesting(false); }
  };

  // Repointe le numéro Twilio sur ce serveur : un numéro resté sur l'URL d'un
  // environnement précédent envoie les appels entrants dans le vide, sans
  // qu'aucun écran du CRM ne puisse le montrer.
  const fixInbound = async () => {
    setFixing(true);
    try {
      // On envoie l'origine du backend telle que le navigateur l'appelle :
      // c'est exactement l'URL que Twilio doit joindre, et elle n'a donc pas à
      // être recopiée à la main dans un champ.
      const origine = new URL(API).origin;
      const { data } = await api.post("/twilio/autoconfigure", { public_base_url: origine });
      setAutoconf(data);
      data.ok ? toast.success(data.detail) : toast.error(data.detail);
      if (data.ok) test();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Configuration impossible");
    } finally { setFixing(false); }
  };

  // Auto-configuration générique : le fournisseur provisionne son compte pour
  // ce serveur (webhooks, application vocale, softphone, numéro) et le backend
  // enregistre ce qu'il a découvert. L'écran ne connaît pas le fournisseur :
  // il suit la capacité `autoconfigure`.
  const autoconfigure = async () => {
    setAutoconfiguring(true);
    try {
      const origine = new URL(API).origin;
      const { data } = await api.post(`/telephony/providers/${selected}/autoconfigure`,
        { public_base_url: origine });
      setAutoconf(data);
      data.ok ? toast.success(data.detail) : toast.error(data.detail);
      // Les champs découverts remplacent ceux de l'écran : l'administrateur
      // voit ce qui a été enregistré sans recharger la page.
      if (data.config) setDetail((d) => ({ ...d, config: data.config }));
      loadList();
      rafraichirSoftphone();
      if (data.ok) test();
    } catch (e) {
      toast.error(e.response?.data?.detail?.detail || e.response?.data?.detail || "Auto-configuration impossible");
    } finally { setAutoconfiguring(false); }
  };

  // Journal vu du fournisseur (capacité `diagnostics`) : appels reçus,
  // webhooks livrés ou non. Quand rien n'arrive au CRM, c'est la seule source.
  const loadDiagnostics = async () => {
    setAlertsLoading(true);
    try {
      const { data } = await api.get(`/telephony/providers/${selected}/diagnostics?limit=30`);
      setAlerts(data.entries || []);
      if (!data.entries?.length) toast.info(`Aucun événement chez ${detail?.label || selected} ces dernières 24 h`);
    } catch (e) {
      toast.error(e.response?.data?.detail?.detail || e.response?.data?.detail || "Journal indisponible");
    } finally { setAlertsLoading(false); }
  };

  // Auto-test générique : le serveur appelle sa propre URL publique comme le
  // ferait le fournisseur — DNS, reverse-proxy, route, signature.
  const selftestProvider = async () => {
    setSelftesting(true); setSelftestResult(null);
    try {
      const origine = new URL(API).origin;
      const { data } = await api.post(`/telephony/providers/${selected}/selftest`, { public_base_url: origine });
      setSelftestResult(data);
      data.ok ? toast.success("URL publique joignable") : toast.error(data.detail);
    } catch (e) {
      toast.error(e.response?.data?.detail?.detail || e.response?.data?.detail || "Auto-test impossible");
    } finally { setSelftesting(false); }
  };

  // Appel entrant simulé : le fournisseur appelle le numéro du CRM depuis le
  // compte. Un sortant qui marche ne prouve rien sur la réception — ce sont
  // deux chemins ; celui-ci prouve numéro → application → CRM sans téléphone.
  const inboundTest = async () => {
    setInboundTesting(true); setSelftestResult(null);
    try {
      const { data } = await api.post(`/telephony/providers/${selected}/inbound-test`, { wait_seconds: 6 });
      setSelftestResult({ ...data, url: data.to ? `Appel de test vers ${data.to}` : "" });
      data.ok ? toast.success("Appel entrant reçu par le CRM") : toast.error(data.detail);
    } catch (e) {
      toast.error(e.response?.data?.detail?.detail || e.response?.data?.detail || "Test impossible");
    } finally { setInboundTesting(false); }
  };

  // Journal d'erreurs de Twilio : la seule source qui explique un appel qui
  // n'arrive jamais jusqu'au CRM (URL injoignable, TwiML refusé, délai dépassé).
  const loadAlerts = async () => {
    setAlertsLoading(true);
    try {
      const { data } = await api.get("/twilio/alerts?limit=15");
      setAlerts(data.alerts || []);
      if (!data.alerts?.length) toast.success("Aucun incident signalé par Twilio");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Journal Twilio indisponible");
    } finally { setAlertsLoading(false); }
  };

  const test = async () => {
    setTesting(true); setTestResult(null);
    try {
      const { data } = await api.post(`/telephony/providers/${selected}/test`);
      setTestResult(data);
      data.ok ? toast.success(data.detail) : toast.error(data.detail);
    } catch (e) {
      toast.error(e.response?.data?.detail?.detail || e.response?.data?.detail || "Test impossible");
    } finally { setTesting(false); }
  };

  const activate = async () => {
    setActivating(true);
    try {
      await api.put("/telephony/provider", { type: selected });
      toast.success(`${detail?.label || selected} est maintenant le fournisseur actif`);
      loadList();
      // Le fournisseur actif change : la barre d'appel doit suivre sans rechargement.
      rafraichirSoftphone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Activation impossible");
    } finally { setActivating(false); }
  };

  const copyWebhook = () => {
    navigator.clipboard.writeText(detail.webhook_url);
    toast.success("URL de webhook copiée");
  };

  const isActive = list.active === selected;
  const configured = detail?.config?.configured;
  // L'aide de mise en service n'existe que pour Ringover.
  const ringover = isRingover(selected);

  return (
    <div data-testid="provider-settings">
      <div className="grid gap-4 items-start grid-cols-[repeat(auto-fit,minmax(320px,1fr))]">
        {/* Colonne gauche : choix du fournisseur + configuration */}
        <div className="flex flex-col gap-3.5 min-w-0">
          <div className={cn(CARD, "p-4")}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <div className="text-[15px] font-medium text-slate-900">Fournisseur téléphonique</div>
                <div className="text-xs text-slate-400 mt-1 text-pretty">
                  Le CRM reste le cerveau (routage, journal, IA) — le fournisseur n'exécute que le transport voix.
                </div>
              </div>
              {detail && (
                <span className={cn(PILL, configured ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500")}
                  data-testid="provider-config-status">
                  <span className={cn(DOT, configured ? "bg-emerald-500" : "bg-slate-400")} />
                  {configured ? "Connecté" : "Non configuré"}
                </span>
              )}
            </div>

            <div className="mt-4 grid gap-2.5 grid-cols-[repeat(auto-fit,minmax(150px,1fr))]">
              {list.providers.map((p) => (
                <button key={p.type} data-testid={`provider-radio-${p.type.toLowerCase()}`}
                  onClick={() => setSelected(p.type)}
                  className={cn("text-left rounded-xl border p-3 transition-colors",
                    selected === p.type ? "border-indigo-500 bg-indigo-50/50" : "border-slate-200 hover:border-slate-300")}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium text-slate-900 truncate">{p.label}</span>
                    <span className={cn("h-4 w-4 shrink-0 rounded-full border-2 flex items-center justify-center",
                      selected === p.type ? "border-indigo-600" : "border-slate-300")}>
                      {selected === p.type && <span className="h-2 w-2 rounded-full bg-indigo-600" />}
                    </span>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {p.active && (
                      <span className={cn(PILL, "bg-slate-900 text-white")} data-testid={`provider-active-badge-${p.type.toLowerCase()}`}>Actif</span>
                    )}
                    <span className={cn(PILL, p.configured ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500")}>
                      {p.configured ? "Configuré" : "Non configuré"}
                    </span>
                  </div>
                </button>
              ))}
            </div>

            {isAdmin && selected && !isActive && (
              <button data-testid="provider-activate-btn" onClick={activate} disabled={activating}
                className={cn(btnPrimary, "mt-3.5 bg-slate-900 hover:bg-slate-800")}>
                <Power size={16} /> {activating ? "Activation…" : `Activer ${detail?.label || selected}`}
              </button>
            )}
          </div>

          {detail && (
            <div className={cn(CARD, "p-4")}>
              <div className="text-[15px] font-medium text-slate-900">Configuration {detail.label}</div>
              <p className="mt-1 mb-3.5 text-xs text-slate-400 text-pretty">
                Les secrets sont chiffrés en base et ne sont jamais exposés au navigateur.
                {!isAdmin && " Lecture seule — seul un administrateur peut modifier."}
              </p>

              {ringover && <RingoverGuide />}

              {/* Sans Auth Token, la signature des webhooks Twilio n'est pas
                  vérifiable : le serveur les refuse (les appels entrants ne
                  passent plus). L'admin doit le voir ici, pas le découvrir en
                  production — et surtout ne pas se retrouver avec des webhooks
                  publics acceptés sans preuve d'origine. */}
              {detail.type === "TWILIO" && detail.config?.webhooks_secured === false && (
                <div data-testid="twilio-webhooks-warning"
                  className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-[13px] text-amber-800">
                  <b>Auth Token manquant.</b> Les webhooks Twilio sont refusés tant qu'ils ne peuvent pas
                  être authentifiés : les appels entrants n'arriveront pas. Renseignez l'Auth Token du
                  compte Twilio ci-dessous (console Twilio → Account Info).
                </div>
              )}

              <div className="grid gap-3 sm:grid-cols-2">
                {(detail.config_fields || []).map((f) => (
                  <Field key={f.key} f={f} value={detail.config[f.key]}
                    onChange={(v) => setVal(f.key, v)} disabled={!isAdmin}
                    help={ringover ? RINGOVER_FIELD_HELP[f.key] : null} />
                ))}
              </div>

              {detail.webhook_url && (
                <div className="mt-4 rounded-xl bg-slate-50 border border-slate-100 p-3">
                  <div className="font-mono text-[10.5px] uppercase tracking-wider text-slate-400 mb-1.5">
                    URL de webhook à déclarer chez {detail.label}
                  </div>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 min-w-0 font-mono text-xs text-slate-600 break-all" data-testid="provider-webhook-url">
                      {detail.webhook_url}
                    </code>
                    <button onClick={copyWebhook} aria-label="Copier l'URL de webhook"
                      className="shrink-0 p-1.5 text-slate-400 hover:text-slate-700"><Copy size={14} /></button>
                  </div>
                  {ringover && (
                    <div className="mt-3 pt-3 border-t border-slate-200 flex flex-col gap-2"
                      data-testid="provider-help-webhook_url">
                      <DashboardPath path={RINGOVER_WEBHOOK_HELP.path} />
                      <p className="text-xs text-slate-500 text-pretty">{RINGOVER_WEBHOOK_HELP.note}</p>
                      <DocLink doc={RINGOVER_WEBHOOK_HELP.doc} />
                    </div>
                  )}
                </div>
              )}

              <div className="mt-4 flex flex-wrap gap-2">
                {isAdmin && (
                  <button data-testid="provider-save-btn" onClick={save} disabled={saving} className={btnPrimary}>
                    <Save size={16} /> {saving ? "Enregistrement…" : "Enregistrer"}
                  </button>
                )}
                <button data-testid="provider-test-btn" onClick={test} disabled={testing} className={btnGhost}>
                  {testing ? <Loader2 size={16} className="animate-spin" /> : <PlugZap size={16} />} Tester la connexion
                </button>
                {detail.type !== "TWILIO" && detail.capabilities?.autoconfigure && isAdmin && (
                  <button data-testid="provider-autoconf-btn" onClick={autoconfigure} disabled={autoconfiguring}
                    title={`Crée chez ${detail.label} ce que le CRM attend (application vocale, webhooks, softphone, numéro) à partir de la clé API, sans rien écraser de ce qui est déjà renseigné`}
                    className={detail.config?.configured ? btnGhost : btnPrimary}>
                    {autoconfiguring ? <Loader2 size={16} className="animate-spin" /> : <PlugZap size={16} />} Configurer {detail.label} automatiquement
                  </button>
                )}
                {detail.type === "TWILIO" && isAdmin && (
                  <button data-testid="provider-fix-inbound-btn" onClick={fixInbound} disabled={fixing}
                    title="Fait pointer le numéro Twilio sur les webhooks de ce serveur"
                    className={testResult?.checks?.inbound_routing?.ok === false ? btnPrimary : btnGhost}>
                    {fixing ? <Loader2 size={16} className="animate-spin" /> : <PlugZap size={16} />} Configurer Twilio automatiquement
                  </button>
                )}
                {detail.type !== "TWILIO" && detail.capabilities?.diagnostics && (
                  <>
                    <button data-testid="provider-diagnostics-btn" onClick={loadDiagnostics} disabled={alertsLoading}
                      title={`Ce que ${detail.label} a vu ces dernières 24 h : appels reçus, webhooks livrés ou en échec`}
                      className={btnGhost}>
                      {alertsLoading ? <Loader2 size={16} className="animate-spin" /> : <PlugZap size={16} />} Journal {detail.label}
                    </button>
                    <button data-testid="provider-selftest-generic-btn" onClick={selftestProvider} disabled={selftesting}
                      title="Appelle l'URL publique du CRM comme le ferait le fournisseur"
                      className={btnGhost}>
                      {selftesting ? <Loader2 size={16} className="animate-spin" /> : <PlugZap size={16} />} Tester les webhooks
                    </button>
                    {isAdmin && (
                      <button data-testid="provider-inbound-test-btn" onClick={inboundTest} disabled={inboundTesting}
                        title={`${detail.label} appelle votre numéro depuis votre compte : prouve la chaîne numéro → application → CRM sans téléphone (coût d'un appel de quelques secondes ; peut faire sonner un agent)`}
                        className={btnGhost}>
                        {inboundTesting ? <Loader2 size={16} className="animate-spin" /> : <PlugZap size={16} />} Simuler un appel entrant
                      </button>
                    )}
                  </>
                )}
                {detail.type === "TWILIO" && (
                  <button data-testid="provider-alerts-btn" onClick={loadAlerts} disabled={alertsLoading}
                    title="Derniers incidents vus par Twilio (URL injoignable, TwiML refusé…)"
                    className={btnGhost}>
                    {alertsLoading ? <Loader2 size={16} className="animate-spin" /> : <PlugZap size={16} />} Incidents Twilio
                  </button>
                )}
                {detail.type === "TWILIO" && (
                  <button data-testid="provider-selftest-btn" onClick={selftest} disabled={selftesting}
                    title="Appelle l'URL publique du CRM comme le ferait Twilio, signature comprise"
                    className={btnGhost}>
                    {selftesting ? <Loader2 size={16} className="animate-spin" /> : <PlugZap size={16} />} Tester les webhooks
                  </button>
                )}
              </div>
              {alerts && (
                <div data-testid="provider-alerts-result"
                  className="mt-3 rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <div className="text-[13px] font-medium text-slate-700">
                    {detail.type === "TWILIO" ? "Incidents signalés par Twilio" : `Journal ${detail.label} (24 h)`}
                    {alerts.length ? ` (${alerts.length})` : ""}
                  </div>
                  {alerts.length === 0 ? (
                    <p className="mt-1 text-xs text-slate-500">
                      {detail.type === "TWILIO" ? "Aucun — Twilio n'a rencontré aucune erreur récente."
                        : `Aucun événement : aucun appel n'a atteint ${detail.label} ces dernières 24 h.`}
                    </p>
                  ) : (
                    <ul className="mt-2 space-y-2">
                      {alerts.map((a) => (
                        <li key={a.sid} className="text-xs text-slate-600 border-t border-slate-200 pt-2 first:border-0 first:pt-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className={cn("font-mono", a.level === "ok" ? "text-emerald-600"
                              : a.level === "info" ? "text-slate-500" : "text-rose-600")}>{a.error_code || a.code}</span>
                            <span className="text-slate-400">{a.date ? new Date(a.date).toLocaleString("fr-FR") : ""}</span>
                          </div>
                          <div className="mt-0.5 text-pretty">{a.text}</div>
                          {a.url && <div className="mt-0.5 font-mono text-[10.5px] text-slate-400 break-all">{a.method} {a.url}</div>}
                          {/* Croisement avec le CRM : la question qui départage
                              « le webhook n'arrive pas » de « le CRM l'a traité ». */}
                          {a.crm && (
                            <div className={cn("mt-1 rounded-md px-2 py-1 text-[11.5px]",
                              a.crm.received ? "bg-emerald-50 text-emerald-800" : "bg-rose-50 text-rose-700")}
                              data-testid="provider-diagnostics-crm">
                              {a.crm.received ? (
                                <>
                                  CRM : reçu
                                  {a.crm.direction ? ` · ${a.crm.direction}` : ""}
                                  {a.crm.from || a.crm.to ? ` · ${a.crm.from || "?"} → ${a.crm.to || "?"}` : ""}
                                  {a.crm.result ? ` · résultat ${a.crm.result}` : ""}
                                  {a.crm.final_destination ? ` (${a.crm.final_destination})` : ""}
                                  {a.crm.answered_by ? ` · décroché par ${a.crm.answered_by}` : ""}
                                  {a.crm.trace?.length > 0 && (
                                    <div className="mt-0.5 opacity-80">{a.crm.trace.join(" → ")}</div>
                                  )}
                                </>
                              ) : "CRM : rien reçu pour cette jambe — le webhook n'est pas arrivé (URL, proxy, signature) ou l'événement date d'avant l'installation"}
                            </div>
                          )}
                          {a.raw && (
                            <details className="mt-0.5">
                              <summary className="cursor-pointer text-[10.5px] text-slate-400">brut</summary>
                              <pre className="mt-0.5 whitespace-pre-wrap break-all font-mono text-[10px] text-slate-500">{a.raw}</pre>
                            </details>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
              {autoconf && (
                <div data-testid="provider-autoconf-result"
                  className={cn("mt-3 rounded-xl border p-3 text-[13px]",
                    autoconf.ok ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                                : "border-rose-200 bg-rose-50 text-rose-800")}>
                  <div>{autoconf.detail}</div>
                  {autoconf.changes?.length > 0 && (
                    <ul className="mt-1.5 list-disc pl-4 space-y-0.5 opacity-90">
                      {autoconf.changes.map((c, i) => <li key={i} className="break-all">{c}</li>)}
                    </ul>
                  )}
                  {autoconf.live?.number_voice_url && (
                    <div className="mt-1.5 font-mono text-[11px] opacity-70 break-all">
                      Numéro (entrant) : {autoconf.live.number_voice_url} [{autoconf.live.number_voice_method}]
                    </div>
                  )}
                  {autoconf.errors?.length > 0 && (
                    <ul className="mt-1.5 list-disc pl-4 space-y-0.5 text-rose-700" data-testid="provider-autoconf-errors">
                      {autoconf.errors.map((c, i) => <li key={i} className="break-all">{c}</li>)}
                    </ul>
                  )}
                  {/* Ce que l'API ne sait pas faire (clé publique, achat de numéro) :
                      dit ici, avec le chemin dans le portail, plutôt que découvert
                      au premier webhook refusé. */}
                  {autoconf.manual?.length > 0 && (
                    <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 p-2 text-amber-800"
                      data-testid="provider-autoconf-manual">
                      <div className="flex items-center gap-1.5 font-medium">
                        <AlertTriangle size={13} className="shrink-0" /> Reste à faire dans le portail
                      </div>
                      <ul className="mt-1 list-disc pl-4 space-y-0.5">
                        {autoconf.manual.map((c, i) => <li key={i} className="break-all">{c}</li>)}
                      </ul>
                    </div>
                  )}
                  {autoconf.info?.softphone_sip_uri && (
                    <div className="mt-1.5 font-mono text-[11px] opacity-70 break-all">
                      URI SIP du softphone (mappage des agents) : {autoconf.info.softphone_sip_uri}
                    </div>
                  )}
                </div>
              )}
              {selftestResult && (
                <div data-testid="provider-selftest-result"
                  className={cn("mt-3 rounded-xl border p-3 text-[13px]",
                    selftestResult.ok ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                                      : "border-rose-200 bg-rose-50 text-rose-800")}>
                  <div>{selftestResult.detail}</div>
                  <div className="mt-1 font-mono text-[11px] opacity-70 break-all">
                    {selftestResult.url}{selftestResult.status ? ` → HTTP ${selftestResult.status}` : ""}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Colonne droite : résultat du test (frise) + capacités */}
        <div className="flex flex-col gap-3.5 min-w-0">
          {testResult && (
            <div className={cn(CARD, "p-4")} data-testid="provider-test-result">
              <div className="text-[15px] font-medium text-slate-900">Journal du test de connexion</div>
              <div className={cn("mt-1 text-xs", testResult.ok ? "text-emerald-600" : "text-rose-600")}>{testResult.detail}</div>
              <div className="mt-4 flex flex-col">
                {Object.entries(testResult.checks || {}).map(([k, v], i, arr) => (
                  <div key={k} className="grid grid-cols-[18px_minmax(0,1fr)] gap-3">
                    <div className="flex flex-col items-center">
                      <span className={cn("h-2.5 w-2.5 rounded-full mt-1.5 shrink-0",
                        v.ok ? "bg-emerald-500" : v.warning ? "bg-amber-500" : "bg-rose-500")} />
                      {i < arr.length - 1 && <span className="flex-1 w-px bg-slate-100" />}
                    </div>
                    <div className="pb-4 min-w-0">
                      <div className="text-[13.5px] text-slate-800">{CHECK_LABELS[k] || k.replace(/_/g, " ")}</div>
                      <div className="text-xs text-slate-400 mt-0.5 text-pretty">
                        {v.detail || v.name || v.status || (v.ok ? "OK" : "Échec")}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {detail && (
            <div className={cn(CARD, "p-4")} data-testid="provider-capabilities">
              <div className="text-[15px] font-medium text-slate-900">Capacités {detail.label}</div>
              <p className="mt-1 mb-3.5 text-xs text-slate-400 text-pretty">
                L'interface du CRM s'adapte automatiquement : les fonctions non supportées sont masquées.
              </p>
              <div className="grid gap-x-4 gap-y-2 sm:grid-cols-2">
                {Object.entries(CAP_LABELS).map(([k, label]) => (
                  <div key={k} className="flex items-center gap-2 text-sm border-b border-slate-50 pb-1.5">
                    {detail.capabilities?.[k]
                      ? <CheckCircle2 size={15} className="shrink-0 text-emerald-500" />
                      : <XCircle size={15} className="shrink-0 text-slate-300" />}
                    <span className={detail.capabilities?.[k] ? "text-slate-700" : "text-slate-400"}>{label}</span>
                  </div>
                ))}
              </div>
              {!detail.capabilities?.live_audio && (
                <div className="mt-3.5 flex items-center gap-1.5 text-xs text-amber-600">
                  <AlertTriangle size={13} className="shrink-0" />
                  Audio live indisponible avec ce fournisseur — analyse IA après appel uniquement.
                </div>
              )}
              {/* La conséquence la plus visible d'une capacité absente : la barre
                  d'appel disparaît pour tout le monde, sans rien dire. Ici, on le dit. */}
              {detail.capabilities?.browser_calls === false && (
                <div className="mt-2 flex items-start gap-1.5 text-xs text-amber-600" data-testid="provider-softphone-warning">
                  <AlertTriangle size={13} className="shrink-0 mt-0.5" />
                  <span>
                    Softphone navigateur indisponible avec cette configuration : la barre d'appel du CRM
                    est masquée pour tous les utilisateurs.
                    {detail.capabilities?.autoconfigure
                      ? " Lancez « Configurer automatiquement » ou renseignez le credential du softphone."
                      : ""}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="mt-4">
        <CountrySettings isAdmin={isAdmin} />
      </div>
    </div>
  );
}
